import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/ui/stat-tile';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import { apiClient } from '@/lib/api-client';

type Row = {
  itemId: string;
  itemSku: string;
  itemName: string;
  standardEstablished: boolean;
  expectedGrams: number | null;
  toleranceGrams: number | null;
  sampleCount: number;
  lastSampleAt: string | null;
};

type CoverageResponse = {
  rows: Row[];
  rollup: {
    variableWeightItems: number;
    standardsEstablished: number;
    coveragePercent: number;
  };
};

function useCoverage() {
  return useQuery({
    queryKey: ['weight', 'coverage'],
    queryFn: async (): Promise<CoverageResponse> => {
      const res = await apiClient.get<CoverageResponse>('/weight/coverage');
      return res.data;
    },
  });
}

function useRecompute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ updated: number; considered: number }> => {
      const res = await apiClient.post<{ updated: number; considered: number }>(
        '/weight/standards/recompute',
        {},
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weight', 'coverage'] }),
  });
}

export function WeightCoveragePage() {
  const t = useT('admin_ui');
  const cov = useCoverage();
  const recompute = useRecompute();
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<{ updated: number; considered: number } | null>(null);

  async function runRecompute(): Promise<void> {
    setError(null);
    try {
      const result = await recompute.mutateAsync();
      setLastRun(result);
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('weight.coverage.title', 'Weight management')}
          </h1>
          {/* SCO strict-mode verification. The nightly recompute takes the most
              recent 1000 samples per item: expected = mean, tolerance = 2σ with a
              5 g floor. The Tolerance column already shows the result, so the
              formula stays here rather than in the copy (RL-331). */}
          <p className="text-sm text-muted-foreground">
            {t(
              'weight.coverage.subtitle',
              'Expected weight of each item, used by SCO to check what the customer puts in the bagging area. Standards are recalculated every night from the latest weighings of each item.',
            )}
          </p>
        </div>
        <Button onClick={runRecompute} disabled={recompute.isPending}>
          {recompute.isPending
            ? t('weight.coverage.recomputing', 'Recomputing…')
            : t('weight.coverage.recompute', 'Recompute standards now')}
        </Button>
      </header>

      {/* Rollup */}
      {cov.data !== undefined && (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile
            label={t('weight.coverage.statItems', 'Variable-weight items')}
            value={String(cov.data.rollup.variableWeightItems)}
          />
          <StatTile
            label={t('weight.coverage.statStandards', 'Standards established')}
            value={String(cov.data.rollup.standardsEstablished)}
          />
          <StatTile
            label={t('weight.coverage.statCoverage', 'Coverage')}
            value={`${cov.data.rollup.coveragePercent}%`}
            highlight
          />
        </section>
      )}

      {lastRun !== null && (
        <p className="text-sm text-muted-foreground">
          {t('weight.coverage.lastRunPrefix', 'Last manual recompute updated')} {lastRun.updated}{' '}
          {t('weight.coverage.lastRunOf', 'of')} {lastRun.considered}{' '}
          {t('weight.coverage.lastRunSuffix', 'eligible items.')}
        </p>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>SKU</TH>
              <TH>{t('weight.coverage.colName', 'Name')}</TH>
              <TH>{t('weight.coverage.colStandard', 'Standard')}</TH>
              <TH className="text-right">{t('weight.coverage.colExpected', 'Expected (g)')}</TH>
              <TH className="text-right">{t('weight.coverage.colTolerance', 'Tolerance (g)')}</TH>
              <TH className="text-right">{t('weight.coverage.colSamples', 'Samples')}</TH>
              <TH>{t('weight.coverage.colLastSample', 'Last sample')}</TH>
            </TR>
          </THead>
          <TBody>
            {cov.isLoading && (
              <TR>
                <TD colSpan={7} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {cov.data?.rows.length === 0 && (
              <TR>
                <TD colSpan={7} className="py-8 text-center text-muted-foreground">
                  {t('weight.coverage.empty', 'No variable-weight items in the catalog yet.')}
                </TD>
              </TR>
            )}
            {cov.data?.rows.map((r) => (
              <TR key={r.itemId}>
                <TD className="font-mono text-xs">{r.itemSku}</TD>
                <TD className="font-medium">{r.itemName}</TD>
                <TD>
                  {r.standardEstablished ? (
                    <Badge variant="success">
                      {t('weight.coverage.established', 'established')}
                    </Badge>
                  ) : (
                    <Badge variant="outline">{t('weight.coverage.pending', 'pending')}</Badge>
                  )}
                </TD>
                <TD className="text-right tabular-nums">{r.expectedGrams ?? '—'}</TD>
                <TD className="text-right tabular-nums">
                  {r.toleranceGrams === null ? '—' : `±${r.toleranceGrams}`}
                </TD>
                <TD className="text-right tabular-nums">{r.sampleCount}</TD>
                <TD className="text-xs text-muted-foreground">
                  {r.lastSampleAt === null ? '—' : new Date(r.lastSampleAt).toLocaleDateString()}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function extractError(err: unknown, t: (key: string, bakedEnglish?: string) => string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return t('weight.coverage.unexpectedError', 'Unexpected error');
}
